"use client";

import { ADVISOR_AVATAR_INITIAL, ADVISOR_NAME } from "@/lib/advisor-identity";

type Size = "xs" | "sm" | "md" | "lg";

const SIZE_PX: Record<Size, number> = { xs: 20, sm: 28, md: 36, lg: 44 };
const FONT_PX: Record<Size, number> = { xs: 12, sm: 12, md: 16, lg: 20 };
const DOT_PX: Record<Size, number> = { xs: 7, sm: 8, md: 10, lg: 12 };

// Drop-shadow tints (green), from the brand shadow tokens: the small marks take the
// resting shadow, the larger ones the lifted one. The fill itself is tokenized too
// (--color-advisor-avatar).
const SHADOW_BY_SIZE: Record<Size, string> = {
  xs: "var(--shadow-brand)",
  sm: "var(--shadow-brand)",
  md: "var(--shadow-brand-hover)",
  lg: "var(--shadow-brand-hover)",
};

export default function AdvisorAvatar({
  size = "md",
  pulse = false,
  active = false,
  initial = ADVISOR_AVATAR_INITIAL,
  ariaLabel = ADVISOR_NAME,
  style,
}: {
  size?: Size;
  pulse?: boolean;
  // Online/active status dot (bottom-right). Folded in from the former main-chat avatar
  // so the whole app renders Mae from this one component.
  active?: boolean;
  initial?: string;
  ariaLabel?: string;
  style?: React.CSSProperties;
}) {
  const dimension = SIZE_PX[size];
  const fontSize = FONT_PX[size];
  const shadow = SHADOW_BY_SIZE[size];
  const dot = DOT_PX[size];
  return (
    <span
      role="img"
      aria-label={ariaLabel}
      style={{
        position: "relative",
        width: dimension,
        height: dimension,
        borderRadius: "var(--radius-full)",
        background: "var(--color-advisor-avatar)",
        color: "var(--color-advisor-avatar-ink)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize,
        flexShrink: 0,
        boxShadow: shadow,
        ...(pulse ? { animation: "advisorAvatarPulse 1.6s ease-in-out infinite" } : {}),
        ...style,
      }}
    >
      {pulse && (
        <style>{`
          @keyframes advisorAvatarPulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}</style>
      )}
      {initial}
      {active && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: dot,
            height: dot,
            borderRadius: "var(--radius-full)",
            background: "var(--color-brand)",
            border: "2px solid var(--color-surface)",
          }}
        />
      )}
    </span>
  );
}
